// The Reports page's dates and hours (UC-19). The plan leaves the presets to
// the dashboard: it turns "Last 7 days" into two trading days and the server
// takes only dates. "Today" is not read from this browser's clock but handed
// in, from the trading day the server gives each shop's day, so a report of
// Today agrees with Home and with the till.
//
// Dates are YYYY-MM-DD strings, done in UTC arithmetic: a trading day is a
// calendar day, and no time zone may move it.

import { isArabic, type Language } from '../i18n/messages';

export type Period = 'today' | 'yesterday' | 'last7' | 'month' | 'lastMonth' | 'range';

export const periods: readonly Period[] = [
	'today',
	'yesterday',
	'last7',
	'month',
	'lastMonth',
	'range',
] as const;

function toDate(day: string): Date {
	return new Date(`${day}T00:00:00Z`);
}

function toDay(date: Date): string {
	return date.toISOString().slice(0, 10);
}

/** The date `n` days after `day` (before, when `n` is negative). */
export function addDays(day: string, n: number): string {
	const date = toDate(day);
	date.setUTCDate(date.getUTCDate() + n);
	return toDay(date);
}

/** The first and last trading days a preset covers, counted back from `today`. */
export function presetRange(period: Period, today: string): { from: string; to: string } {
	switch (period) {
		case 'yesterday': {
			const day = addDays(today, -1);
			return { from: day, to: day };
		}
		case 'last7':
			return { from: addDays(today, -6), to: today };
		case 'month':
			return { from: `${today.slice(0, 7)}-01`, to: today };
		case 'lastMonth': {
			const last = addDays(`${today.slice(0, 7)}-01`, -1);
			return { from: `${last.slice(0, 7)}-01`, to: last };
		}
		default:
			return { from: today, to: today };
	}
}

/** How many days a range holds, both ends counted. */
export function daySpan(from: string, to: string): number {
	return Math.round((toDate(to).getTime() - toDate(from).getTime()) / 86_400_000) + 1;
}

const locale = (language: Language) => (isArabic(language) ? 'ar-EG-u-nu-latn' : 'en-US');

/** A date as "27 Sep 2026", or "27 سبتمبر 2026": day first in both languages. */
function dayParts(day: string, language: Language): { day: string; month: string; year: string } {
	const parts = new Intl.DateTimeFormat(locale(language), {
		day: 'numeric',
		month: isArabic(language) ? 'long' : 'short',
		year: 'numeric',
		timeZone: 'UTC',
	}).formatToParts(toDate(day));
	const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
	return { day: part('day'), month: part('month'), year: part('year') };
}

/** "24 Sep": a day within a year the reader already has. */
export function formatDay(day: string, language: Language): string {
	const p = dayParts(day, language);
	return `${p.day} ${p.month}`;
}

/** "27 Sep 2026", "21 Sep – 27 Sep 2026", or "28 Dec 2025 – 3 Jan 2026". */
export function formatRange(from: string, to: string, language: Language): string {
	const a = dayParts(from, language);
	const b = dayParts(to, language);
	if (from === to) return `${a.day} ${a.month} ${a.year}`;
	if (a.year === b.year) return `${a.day} ${a.month} – ${b.day} ${b.month} ${b.year}`;
	return `${a.day} ${a.month} ${a.year} – ${b.day} ${b.month} ${b.year}`;
}

/** An hour of the day on a twelve-hour clock: "11:00 AM", "11:00 ص". */
export function hourLabel(hour: number, am: string, pm: string): string {
	return `${hour % 12 || 12}:00 ${hour < 12 ? am : pm}`;
}

/** Sorts hours in the order a trading day runs them, from the hour it starts. */
export function byTradingHour(dayStartHour: number): (a: number, b: number) => number {
	return (a, b) => ((a - dayStartHour + 24) % 24) - ((b - dayStartHour + 24) % 24);
}

/**
 * Each value as a share of the largest, for a set of meters: the peak reads at
 * full strength and the rest are dimmed, so the strongest stands out before a
 * figure is read.
 */
export function withBars<T>(
	list: readonly T[],
	measure: (entry: T) => number,
): (T & { bar: number; dim: boolean })[] {
	const values = list.map(measure);
	const max = Math.max(0, ...values);
	const peak = values.indexOf(max);
	return list.map((entry, index) => ({
		...entry,
		bar: max ? ((values[index] ?? 0) / max) * 100 : 0,
		dim: index !== peak,
	}));
}

/** What a report row needs to be told apart from the others. */
export interface ItemRowIdentity {
	itemId?: string | null;
	name: string;
	soldBy?: string | null;
}

/**
 * The key of an item's row. One item can come back as two rows, its portions
 * and its pieces apart: a sale the stale track recorded with no weight counts
 * as a piece (UC-19.R13), so the key holds the row's kind.
 */
export function itemRowKey(item: ItemRowIdentity): string {
	return `${item.itemId || item.name}:${item.soldBy || 'piece'}`;
}

/**
 * A row's test id: a piece row keeps the one it always had, and a weighed row
 * of an item that also has a piece row is told apart by its kind.
 */
export function itemRowId(item: ItemRowIdentity, rows: readonly ItemRowIdentity[]): string {
	const id = item.itemId || item.name;
	const twin = rows.some((other) => other !== item && (other.itemId || other.name) === id);
	return twin && item.soldBy === 'weight' ? `${id}-weight` : id;
}
